import { Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, ActivatedRouteSnapshot, RouterStateSnapshot } from "@angular/router";
import {
    ITCIAppEnvControlProviderRequest,
    ITCIAppEnvControlProviderResponse,
    ITCIApplicationDeployRequest,
    ITCIApplicationDeployResponse,
    TCI_APP_DETAIL_ENV_MENU_TYPE,
    TCI_ENV_CTRL_ACTION_TYPE,
    TCIApplicationDeploymentMode,
    TCIApplicationDeploymentNotification,
    TCIApplicationDeploymentStatus,
    TCIAppStatusCategory,
    TCITenantAppDetailEnvMenu,
    TCITenantSelectorType,
    TCITenantAppType,
    USER_ROLES,
    TCI_APP_DEPLOYMENT_STAGE
} from "@tci/common";
import { NgToastrService } from "../../../ng-shared/ng-components/ng-toastr/ng-toastr.service";
import { ITCIAppRefreshNotification } from "../../../ng-shared/ng-services/ng-app-change-notification.service";
import { NgToolsService } from "../../../ng-shared/ng-util/ng-tools.service";
import {
    ITCITenantRouteDeactivator,
    TciTenantRouteDeActivator
} from "../../../ng-tenant/tci-tenant-route-de-activator.service";

import { BaseAppDetailEnvService } from "./base-app-detail-env.service";
import { NgSubmenuLeftService } from "../../ng-submenu-left/ng-submenu-left.service";
import { NgAppFeatureToggleService } from "../../../ng-shared/ng-services/ng-app-feature-toggle.service";
import { AbstractTCITenantComponent } from "../../../ng-tenant/tci-tenant-component";
import { BaseAppDetailMenuComponentInput, BaseAppDetailMenuComponentOutput } from "../../ng-app-details-menu";

import { Observable, Subject, Subscription } from "rxjs";
import * as lodash from "lodash";

@Component({
    selector: TCITenantSelectorType.TCI_APP_DETAIL_ENVIRONMENT.toString(),
    templateUrl: "./base-app-detail-env.component.html",
    styleUrls: ["./base-app-detail-env.component.less"]
})
// @ts-ignore
export class BaseAppDetailEnvComponent<E, I extends BaseAppDetailMenuComponentInput, O extends BaseAppDetailMenuComponentOutput<E>>
    extends AbstractTCITenantComponent<E, I, O> implements ITCITenantRouteDeactivator, OnInit, OnDestroy {

    private submenuLeftRouteId: string;
    private currentRoute: string;

    private subMenuItemInput: BaseAppDetailMenuComponentInput;

    private changesAvailable = false;
    private appStatus: ITCIApplicationDeployResponse;

    // For canDeactivate
    private showCanDeactivateDialog = false;
    private canDeactivateDialogResponse$: Subject<boolean> = new Subject();

    // Subscriptions collection
    private subscriptions: Subscription[] = [];

    private isRORoleUser = false;
    private showWarningDialog = false;
    private isLive = false;
    private isNeedPullingAppStatus = true;
    isProperties: boolean;
    isInApplicationVariablesTab: boolean;
    private isOnPremise: boolean;

    constructor(
        private route: ActivatedRoute,
        private routeDeactivator: TciTenantRouteDeActivator,
        private menuSvc: NgSubmenuLeftService,
        private envCtrlSvc: BaseAppDetailEnvService,
        private toastr: NgToastrService,
        private featureToggleService: NgAppFeatureToggleService,
        private tools: NgToolsService,
    ) {
        super();
    }

    ngOnInit(): void {
        this.currentRoute = this.input.componentOption.getRouteId();
        this.routeDeactivator.addRouteId(this.currentRoute, this);

        if (this.input.application.remoteManagement) {
            this.isOnPremise = this.input.application.remoteManagement;
        } else {
            this.isOnPremise = false;
        }
        // Request tenant information
        this.subscriptions.push(this.envCtrlSvc.getStatusSubject()
            .filter(res => res !== TCI_ENV_CTRL_ACTION_TYPE.REFRESH)
            .subscribe(() => this.checkForChanges()));
        this.checkForChanges();

        // Track route updates for submenu
        this.subscriptions.push(this.route.params.subscribe(params => {
            this.submenuLeftRouteId = params["submenuleft"];
            if (this.submenuLeftRouteId) {
                this.menuSvc.validateMenuItem(this.input.application, this.currentRoute, this.submenuLeftRouteId)
                    .subscribe(valid => {
                        if (valid) {
                            this.isInApplicationVariablesTab =
                                this.submenuLeftRouteId === TCITenantAppDetailEnvMenu.TCI_MENU_APP_DETAILS_ENV_APP_VARS.getRouteId();
                            this.mountMenuItemComponent();
                        }
                    });
            }
        }));

        this.subscriptions.push(this.envCtrlSvc.getAppRefreshNotifSubj()
            .filter((refr: ITCIAppRefreshNotification) => refr.success && refr.app.id === this.input.application.id)
            .subscribe(() => {
                this.resetUpdate();
            }));

        // Request current state
        const stateRequest: ITCIApplicationDeployRequest = {
            mode: TCIApplicationDeploymentMode.MODE_APP_DETAILS,
            appId: this.input.application.id,
            application: this.input.application
        };

        this.subscriptions.push(
            this.envCtrlSvc
                .getTenantDeployProvider(this.input.tenantAppType)
                .isBusy(stateRequest)
                .subscribe(busy => {
                    if (busy) {
                        this.checkForChanges();
                    }
                })
        );

        this.initAppStatus();

        const thisObj = this;
        this.envCtrlSvc.getUserAuthForEnv()
            .subscribe(userAuth => {
                thisObj.isRORoleUser = (userAuth.role === USER_ROLES.RO);
            });
        this.isLive = this.input.application.publishStage === TCI_APP_DEPLOYMENT_STAGE.PUBLISHED;

        this.envCtrlSvc.initApplicationProperties(this.generateReqInput(TCI_ENV_CTRL_ACTION_TYPE.APP_PROPERTIES)).subscribe(res => {
            this.isProperties = (res.data.properties.length > 0);
        });
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());

        // Unregister the activator
        this.routeDeactivator.removeRouteId(this.currentRoute);
    }

    initAppStatus() {
        this.appStatus = this.envCtrlSvc.getAppStatus(this.input.application);
        this.checkIsNeedPullingAppStatus();

        this.subscriptions.push(this.envCtrlSvc.getTenantDeployProvider(this.input.application.type).getStatusSubject()
            .subscribe(res => {
                this.envCtrlSvc.setAppStatus(res);
            }));
    }

    checkIsNeedPullingAppStatus() {
        // Note: if the flogo app status is "Not deployed", then do not need to pull app status.
        if (this.input.application.type.equals(TCITenantAppType.flogo)
            && this.appStatus.status
            && this.appStatus.status.getLabel().toLowerCase() === "Not deployed".toLowerCase()) {
            this.isNeedPullingAppStatus = false;
        }
    }

    mountMenuItemComponent(): void {
        this.isLive = this.input.application.publishStage === TCI_APP_DEPLOYMENT_STAGE.PUBLISHED;
        if (this.input && this.input.application) {
            const subMenuItemSelector = TCITenantAppDetailEnvMenu.getAllMenuItems(TCI_APP_DETAIL_ENV_MENU_TYPE.MAIN_MENU)
                .find(item => item.getRouteId() === this.submenuLeftRouteId);

            if (
                !this.subMenuItemInput || (
                    this.subMenuItemInput.componentOption &&
                    subMenuItemSelector.getComponentSelectorType().toString() !== this.subMenuItemInput.componentSelector.toString()
                )
            ) {
                this.subMenuItemInput = new BaseAppDetailMenuComponentInput(
                    this.input.tenantAppType,
                    subMenuItemSelector.getComponentSelectorType(),
                    this.input.application,
                    this.input.userService,
                    this.featureToggleService,
                    this.input.mode,
                    this.input.componentOption);
            }
        }
    }

    generateReqInput(action_type: TCI_ENV_CTRL_ACTION_TYPE): ITCIAppEnvControlProviderRequest {
        return this.envCtrlSvc.generateReqInput(action_type, this.input.application);
    }

    checkForChanges() {
        this.envCtrlSvc.getChanges(this.input.application)
            .subscribe(res => this.changesAvailable = Object.values(res).some(val => val === true));
    }

    resetUpdate() {
        this.envCtrlSvc.cleanChanges();
        this.envCtrlSvc.getStatusSubject().next(TCI_ENV_CTRL_ACTION_TYPE.REFRESH);
        this.checkForChanges();
    }

    isWarningDialog() {
      this.showWarningDialog = !this.showWarningDialog;
    }

    closeIsLiveDialog() {
        this.showWarningDialog = false;
    }

    update() {
        if (this.isLive) {
            this.isWarningDialog();
        }
        this.envCtrlSvc.getChanges(this.input.application).subscribe(res => {
            let canPushObs: Observable<ITCIAppEnvControlProviderResponse>, request: ITCIAppEnvControlProviderRequest;
            if (res.canPushApplicationProperties) {
                request = this.generateReqInput(TCI_ENV_CTRL_ACTION_TYPE.APP_PROPERTIES);
                request.data = this.envCtrlSvc.getTabData(TCI_ENV_CTRL_ACTION_TYPE.APP_PROPERTIES)["postConfigData"];
                const pushData = lodash.omit(request, ["application"]);
                canPushObs = this.envCtrlSvc.pushApplicationProperties(pushData);
            }

            if (res.canPushAppVPNConnection) {
                request = this.generateReqInput(TCI_ENV_CTRL_ACTION_TYPE.APP_VPN);
                canPushObs = this.envCtrlSvc.pushAppVPNConnection(request).map(upd => {
                    this.input.application.vpnConnectionId = upd.defaultVPNConnectionId;
                    return upd;
                });
            }

            if (res.canPushEngineProperties) {
                request = this.generateReqInput(TCI_ENV_CTRL_ACTION_TYPE.ENGINE_PROPERTIES);
                request.data = this.envCtrlSvc.getTabData(TCI_ENV_CTRL_ACTION_TYPE.ENGINE_PROPERTIES)["postConfigData"];
                const pushData = lodash.omit(request, ["application"]);
                canPushObs = this.envCtrlSvc.pushEngineProperties(pushData);
            }

            if (res.canPushHybridConnectivity) {
                request = this.generateReqInput(TCI_ENV_CTRL_ACTION_TYPE.HYBRID_CONNECTIVITY);
                canPushObs = this.envCtrlSvc.pushHybridConnectivity(request)
                    .map(upd => {
                        this.input.application.tibTunnelAccessKey = upd.defaultAccessKey;
                        return upd;
                    });
            }
            if (res.canPushAppAutoScale) {
                request = this.generateReqInput(TCI_ENV_CTRL_ACTION_TYPE.APP_AUTO_SCALE);
                request.autoScaleConfig = this.envCtrlSvc.getTabData(TCI_ENV_CTRL_ACTION_TYPE.APP_AUTO_SCALE);
                canPushObs = this.envCtrlSvc.pushAppAutoScale(request)
                    .map(upd => {
                        this.input.application.autoScaleConfig = upd.autoScaleConfig;
                        return upd;
                    });
            }

            const currentAppStatus = this.envCtrlSvc.getTenantDeployProvider(request.tenantType).getStatusSnapshot(this.input.application);
            const callBack = (input) => {
                return Observable.create(obs => {
                    canPushObs
                        .catch((err) => {
                            const errorMsg = err.msg || err.json().errorDetail;
                            if (errorMsg) {
                                this.toastr.error(errorMsg);
                            }
                            const cbResponse: ITCIApplicationDeployResponse = {
                                success: false,
                                status: currentAppStatus.status,
                                notificationType: currentAppStatus.notificationType,
                                appId: currentAppStatus.appId
                            };
                            return Observable.of(cbResponse);
                        })
                        .subscribe((updRes) => {
                            // app is preparing
                            if (updRes.success) {
                                const cbResponse: ITCIApplicationDeployResponse = {
                                    success: true,
                                    status: new TCIApplicationDeploymentStatus("Scaling", TCIAppStatusCategory.IN_PROGRESS),
                                    notificationType: TCIApplicationDeploymentNotification.NOTIFICATION_RESULT_APP_CHANGES,
                                    appId: request.appId
                                };
                                obs.next(cbResponse);
                            } else {
                                obs.next(updRes);
                            }
                            obs.complete();
                        });
                });
            };
            const reqData: ITCIApplicationDeployRequest = {
                application: request.application,
                mode: TCIApplicationDeploymentMode.MODE_APP_DETAILS,
                appId: request.appId
            };
             if (this.isNeedPullingAppStatus) {
                 this.envCtrlSvc.getTenantDeployProvider(request.tenantType).pushAppChanges(reqData, callBack)
                     .subscribe(appStatus => {
                         // Note: when notification type is 0(refresh), it means scaling finished, scaling status is false.
                         if (appStatus.notificationType === TCIApplicationDeploymentNotification.NOTIFICATION_REFRESH) {
                             this.envCtrlSvc.setAppStatus(appStatus);
                         }
                     });
             } else {
                 callBack(reqData).subscribe(() => {
                     this.envCtrlSvc.cleanChanges();
                     this.checkForChanges();
                });
             }
        });
    }
    // IPAS-11262: This feature is moved to next release.
    downloadAppProperties() {
        this.envCtrlSvc.getAppProperties(this.generateReqInput(TCI_ENV_CTRL_ACTION_TYPE.APP_PROPERTIES))
            .subscribe((res) => {
                const properties = res.text();
            if (properties.length > 0) {
                const fileName = this.input.application.name + ".properties";
                this.tools.download(fileName, properties, "text/plain");
            }
            return res;
        }, error => {
                console.log("Error getting configuration for the app:", error);
            });
    }

    // Deactivator
    canDeactivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
        // if (this.navigatingOutside && this.changesAvailable) {
        if (this.changesAvailable) {
            this.showCanDeactivateDialog = true;
            return Observable.create(obs => {
                const sub = this.canDeactivateDialogResponse$
                    .do(() => this.showCanDeactivateDialog = false)
                    .subscribe(res => {
                        if (res) { // Clean the changes
                            this.envCtrlSvc.cleanChanges();
                            this.checkForChanges();
                        }

                        obs.next(res);
                        obs.complete();
                    });
                this.subscriptions.push(sub);
            });
        }
        return Observable.of(true);
    }

    dialogResponse(resp: boolean) {
        this.canDeactivateDialogResponse$.next(resp);
    }
}
